interface ChatModelDefinition {
  id: `${string}/${string}`;
  name: string;
  description: string;
  contextWindowTokens: number;
  maxOutputTokens: number;
  supportsImageInput: boolean;
  thinkingEfforts: readonly SelectedChatThinkingEffort[];
}

const defaultThinkingEffortValues = ["default"] as const;
const openAiThinkingEffortValues = [
  "default",
  "low",
  "medium",
  "high",
] as const;
const googleFlashThinkingEffortValues = [
  "default",
  "low",
  "medium",
  "high",
] as const;
const googleProThinkingEffortValues = ["default", "low", "high"] as const;
const anthropicThinkingEffortValues = [
  "default",
  "low",
  "medium",
  "high",
  "max",
] as const;

export const selectedChatThinkingEffortValues = [
  "default",
  "low",
  "medium",
  "high",
  "max",
] as const;

export type SelectedChatThinkingEffort =
  (typeof selectedChatThinkingEffortValues)[number];

export interface ChatThinkingEffort {
  value: SelectedChatThinkingEffort;
  label: string;
  description: string;
}

export const DEFAULT_CHAT_THINKING_EFFORT: SelectedChatThinkingEffort =
  "default";

export const chatThinkingEfforts: ChatThinkingEffort[] = [
  {
    value: "default",
    label: "Default",
    description: "Use the model's default thinking behavior.",
  },
  {
    value: "low",
    label: "Low",
    description: "Faster responses with a smaller reasoning budget.",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Balanced reasoning budget for most tasks.",
  },
  {
    value: "high",
    label: "High",
    description: "More reasoning for harder tasks.",
  },
  {
    value: "max",
    label: "Max",
    description: "Largest available reasoning budget.",
  },
];

const chatModelDefinitions = [
  {
    id: "openai/gpt-5.4",
    name: "OpenAI GPT-5.4",
    description: "OpenAI's GPT-5.4 model",
    contextWindowTokens: 1_050_000,
    maxOutputTokens: 128_000,
    supportsImageInput: true,
    thinkingEfforts: openAiThinkingEffortValues,
  },
  {
    id: "openai/gpt-5.3-codex",
    name: "OpenAI GPT-5.3 Codex",
    description: "OpenAI's GPT-5.3 Codex model",
    contextWindowTokens: 400_000,
    maxOutputTokens: 128_000,
    supportsImageInput: false,
    thinkingEfforts: openAiThinkingEffortValues,
  },
  {
    id: "openai/gpt-5-mini",
    name: "OpenAI GPT-5 Mini",
    description: "Faster, lower-cost GPT-5 family model",
    contextWindowTokens: 400_000,
    maxOutputTokens: 128_000,
    supportsImageInput: true,
    thinkingEfforts: openAiThinkingEffortValues,
  },
  {
    id: "google/gemini-3-flash",
    name: "Google Gemini 3 Flash",
    description: "Google's fast Gemini 3 model",
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 65_000,
    supportsImageInput: true,
    thinkingEfforts: googleFlashThinkingEffortValues,
  },
  {
    id: "google/gemini-3-pro-preview",
    name: "Google Gemini 3 Pro",
    description: "Google's higher-capability Gemini 3 model",
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 64_000,
    supportsImageInput: true,
    thinkingEfforts: googleProThinkingEffortValues,
  },
  {
    id: "anthropic/claude-haiku-4.5",
    name: "Anthropic Claude Haiku 4.5",
    description: "Anthropic's fastest Claude 4.5 model",
    contextWindowTokens: 200_000,
    maxOutputTokens: 64_000,
    supportsImageInput: true,
    thinkingEfforts: anthropicThinkingEffortValues,
  },
  {
    id: "anthropic/claude-sonnet-4.5",
    name: "Anthropic Claude Sonnet 4.5",
    description: "Anthropic's balanced Claude 4.5 model",
    contextWindowTokens: 1_000_000,
    maxOutputTokens: 64_000,
    supportsImageInput: true,
    thinkingEfforts: anthropicThinkingEffortValues,
  },
  {
    id: "moonshotai/kimi-k2.5",
    name: "MoonshotAI Kimi K2.5",
    description: "MoonshotAI's Kimi K2.5 model",
    contextWindowTokens: 262_114,
    maxOutputTokens: 262_114,
    supportsImageInput: false,
    thinkingEfforts: defaultThinkingEffortValues,
  },
] as const satisfies ReadonlyArray<ChatModelDefinition>;

export type SelectedChatModel = (typeof chatModelDefinitions)[number]["id"];

export interface ChatModel {
  id: SelectedChatModel;
  name: string;
  description: string;
  contextWindowTokens: number;
  maxOutputTokens: number;
  supportsImageInput: boolean;
  thinkingEfforts: readonly SelectedChatThinkingEffort[];
}

export const selectedChatModelValues = chatModelDefinitions.map(
  (model) => model.id
) as [SelectedChatModel, ...SelectedChatModel[]];

const selectedChatModelSet = new Set<SelectedChatModel>(
  selectedChatModelValues
);
const selectedChatThinkingEffortSet = new Set<SelectedChatThinkingEffort>(
  selectedChatThinkingEffortValues
);
const chatModelDefinitionById = new Map<SelectedChatModel, ChatModelDefinition>(
  chatModelDefinitions.map((model) => [model.id, model])
);

export const DEFAULT_CHAT_MODEL: SelectedChatModel = chatModelDefinitions[0].id;

export const CHAT_MEMORY_MODEL: SelectedChatModel = "google/gemini-3-flash";

export const chatModels: ChatModel[] = chatModelDefinitions.map((model) => ({
  ...model,
}));

export function isSelectedChatModel(
  value: unknown
): value is SelectedChatModel {
  return (
    typeof value === "string" &&
    selectedChatModelSet.has(value as SelectedChatModel)
  );
}

export function isSelectedChatThinkingEffort(
  value: unknown
): value is SelectedChatThinkingEffort {
  return (
    typeof value === "string" &&
    selectedChatThinkingEffortSet.has(value as SelectedChatThinkingEffort)
  );
}

export function supportsChatModelImageInput(model: SelectedChatModel): boolean {
  return chatModelDefinitionById.get(model)?.supportsImageInput ?? false;
}

export function supportsChatModelThinkingEffort(
  model: SelectedChatModel
): boolean {
  return getAvailableChatThinkingEfforts(model).length > 1;
}

export function getChatModelContextWindowTokens(
  model: SelectedChatModel
): number {
  return chatModelDefinitionById.get(model)?.contextWindowTokens ?? 0;
}

export function getChatModelMaxOutputTokens(model: SelectedChatModel): number {
  return chatModelDefinitionById.get(model)?.maxOutputTokens ?? 0;
}

export function getAvailableChatThinkingEfforts(
  model: SelectedChatModel
): SelectedChatThinkingEffort[] {
  return [
    ...(chatModelDefinitionById.get(model)?.thinkingEfforts ??
      defaultThinkingEffortValues),
  ];
}

export function resolveChatThinkingEffort(
  model: SelectedChatModel,
  desiredEffort: SelectedChatThinkingEffort
): SelectedChatThinkingEffort {
  const available = getAvailableChatThinkingEfforts(model);
  if (available.includes(desiredEffort)) {
    return desiredEffort;
  }

  const fallbackOrderByDesired: Record<
    SelectedChatThinkingEffort,
    SelectedChatThinkingEffort[]
  > = {
    default: ["default"],
    low: ["default"],
    medium: ["high", "low", "default"],
    high: ["medium", "low", "default"],
    max: ["high", "medium", "low", "default"],
  };

  return (
    fallbackOrderByDesired[desiredEffort].find((effort) =>
      available.includes(effort)
    ) ?? DEFAULT_CHAT_THINKING_EFFORT
  );
}
